/**
 * <burn-marquee>
 *
 * A giant horizontal marquee that is scroll-velocity reactive: the faster the visitor
 * scrolls the page, the faster the text travels and the more it skews. Driven entirely by
 * requestAnimationFrame for smooth control. Static under prefers-reduced-motion.
 *
 * Markup: the track holds several identical [data-bm-group] copies; the loop wraps on one
 * group's width so it tiles seamlessly.
 */
const BM_REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class BurnMarquee extends HTMLElement {
  #track = null;
  #groupWidth = 0;
  #offset = 0;
  #base = 60;
  #dir = -1;
  #boost = 0;
  #raf = 0;
  #last = 0;
  #lastScrollY = 0;
  #onScroll = null;
  #io = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-bm-track]');
    if (!this.#track) return;

    this.#base = parseFloat(this.dataset.speed || '60') || 60;
    this.#dir = this.dataset.direction === 'right' ? 1 : -1;
    this.#measure();
    window.addEventListener('resize', this.#measure, { passive: true });

    if (BM_REDUCE_MOTION.matches) return;

    this.#lastScrollY = window.scrollY;
    this.#onScroll = () => {
      const y = window.scrollY;
      this.#boost = Math.min(this.#boost + Math.abs(y - this.#lastScrollY) * 6, 1400);
      this.#lastScrollY = y;
    };
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.#play();
        else this.#pause();
      },
      { threshold: 0 }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#pause();
    window.removeEventListener('resize', this.#measure);
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    this.#io?.disconnect();
  }

  #measure = () => {
    const group = this.querySelector('[data-bm-group]');
    this.#groupWidth = group ? group.getBoundingClientRect().width : 0;
  };

  #play() {
    if (this.#raf) return;
    this.#last = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #pause() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  #tick = (now) => {
    const dt = Math.min((now - this.#last) / 1000, 0.05);
    this.#last = now;

    const speed = this.#base + this.#boost; // px/sec
    this.#offset += speed * dt;
    if (this.#groupWidth > 0) this.#offset %= this.#groupWidth;

    const x = this.#dir === -1 ? -this.#offset : this.#offset - this.#groupWidth;
    const skew = Math.max(-7, Math.min(7, (this.#boost / 1400) * 7)) * (this.#dir === -1 ? -1 : 1);
    this.#track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0) skewX(${skew.toFixed(2)}deg)`;

    this.#boost *= 0.92; // decay the scroll boost back to base speed
    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('burn-marquee')) {
  customElements.define('burn-marquee', BurnMarquee);
}
